/* assets/pbm-quick-order-engine.js  PBM Quick Order line reader + matcher (v3), window.PBMQuickOrderEngine.
   A line-for-line port of the tested Python reference engine (qo_engine_draft5.py); function names, comments and
   the order of every check follow the Python, so the two can be read side by side. Pure logic: no DOM, no network,
   no storage. ES5 only (var/function, no lookbehind, no inline regex flags, no named groups, no optional chaining).
   API
     buildIndex(rows)         -> ix. rows = v3 index rows:
                                 [variantId, handle, sku, mpn, title, priceCents, gated 0|1, family, barcode, vendor, packTitle]
                                 family = 0, or an array of the sizes' variant ids (a listing-only family parent).
     readBlock(text, ix)      -> entries [{raw, noise, parts, desc, text, qty, src, unit, extra, whole, short, split,
                                 sentence, link, slash_pair, many_keys, qty_is_key, signature, continued, clause, ellipsis}]
     match(entry, ix, search) -> Promise of {state, options, flags, fixed}; state is one of STATES; options are index items.
                                 search(query, fix) -> Promise of {items: [{h, sku, bc}], fixed: string|null}. Blank
                                 queries never reach it. A rejected search rejects the match (the page decides).
     norm(s), clean(s), FLAGS (every flag name the matcher can add), STATES, QTY_FLAGS.
   Item: {id, h, sku, mpn, title, price, vendor, gated, kids (null, or the sizes' handles of a family parent),
          pack, pack_n, ns, nm, bc, tw, t2}.
   Fixed in both engines (2026-10-24): match() no longer clears entry.unit (it can run again on Retry); readBlock() could
   recurse without end, and then throws RangeError, on some one-part-number sentences ("need 4 X and 2 of the Y also
   1 41025"), so the page wraps it in try/catch. Not the same: ASCII-only digits and white space (no
   fullwidth digits, U+FEFF counts as white space), and \b next to non-Latin letters; "shared number" options come in index
   order (the Python's order changes with PYTHONHASHSEED). Parity harness: scripts/parity/run.sh. */
(function () {
  'use strict';

  // ------------------------------------------------------------------ small helpers (Python str / list / set idioms)
  function norm(s) { return String(s == null ? '' : s).toUpperCase().replace(/[^A-Z0-9]/g, ''); }
  function dict(o) { var d = Object.create(null), k; for (k in o) { if (Object.prototype.hasOwnProperty.call(o, k)) d[k] = o[k]; } return d; }
  function mkset(a) { var s = new Set(), i; for (i = 0; i < a.length; i++) s.add(a[i]); return s; }
  function subset(a, b) { var ok = true; a.forEach(function (x) { if (!b.has(x)) ok = false; }); return ok; }
  function replace_all(s, a, b) { return s.split(a).join(b); }
  function split_ws(s) { s = s.trim(); return s === '' ? [] : s.split(/\s+/); }                 // str.split()
  function stripc(s, chars) {                                                                    // str.strip(chars)
    var a = 0, b = s.length;
    while (a < b && chars.indexOf(s.charAt(a)) >= 0) a++;
    while (b > a && chars.indexOf(s.charAt(b - 1)) >= 0) b--;
    return s.slice(a, b);
  }
  function rstripc(s, chars) { var b = s.length; while (b > 0 && chars.indexOf(s.charAt(b - 1)) >= 0) b--; return s.slice(0, b); }
  function lstrip0(s) { return s.replace(/^0+/, ''); }
  function isdigit(s) { return /^[0-9]+$/.test(s); }
  function words_of(rx, s) { return s.match(rx) || []; }                                         // re.findall, no groups
  function sorted_by(arr, keyfn) {                                                               // sorted(key=...), stable
    return arr.map(function (x, i) { return { x: x, k: keyfn(x), i: i }; })
      .sort(function (a, b) { return (a.k - b.k) || (a.i - b.i); }).map(function (d) { return d.x; });
  }
  function same_tuple(a, b) {
    var i;
    if (a.length !== b.length) return false;
    for (i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }
  // CPython shares every one-character string, so "c is not qc" also drops other cells holding the same single character.
  var CELLS1 = Object.create(null);
  function W(s) {
    if (s.length === 0 || (s.length === 1 && s.charCodeAt(0) < 256)) return CELLS1[s] || (CELLS1[s] = { s: s });
    return { s: s };
  }
  function two_letter_words(s) {                       // two-letter words: a run of letters and digits that is exactly two letters
    return words_of(/[A-Za-z0-9]+/g, s).filter(function (w) { return /^[A-Za-z]{2}$/.test(w); });
  }

  // Python's \w is Unicode-aware, JS's is ASCII only: WORDCH adds the Latin, Greek and Cyrillic letters (accented names).
  var WORDCH = '\\w\\u00aa\\u00b5\\u00ba\\u00c0-\\u00d6\\u00d8-\\u00f6\\u00f8-\\u02c1\\u0370-\\u03ff\\u0400-\\u052f\\u1e00-\\u1eff';
  var WORD_RX = new RegExp('[' + WORDCH + ']');
  var DASH_RX = /[\u2010\u2011\u2012\u2013\u2014\u2212\ufe63\uff0d]/g;
  var UNITS = dict({ ea: 'EA', each: 'EA', pc: 'EA', pcs: 'EA', piece: 'EA', pieces: 'EA', unit: 'EA', units: 'EA',
    bx: 'BX', box: 'BX', boxes: 'BX', cs: 'CS', 'case': 'CS', cases: 'CS', pk: 'PK', pack: 'PK', packs: 'PK', pkg: 'PK',
    dz: 'DZ', doz: 'DZ', dozen: 'DZ', pr: 'PR', pair: 'PR', pairs: 'PR', gross: 'GR', ctn: 'CTN', carton: 'CTN', cartons: 'CTN',
    bag: 'BG', bags: 'BG', bg: 'BG', convunits: 'CONV' });
  var NUMWORD = dict({ a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    eleven: 11, twelve: 12, fifteen: 15, twenty: 20, thirty: 30, fifty: 50, hundred: 100 });
  function numword(x) { return x in NUMWORD ? NUMWORD[x] : null; }
  function unit_of(x) { x = (x || '').toLowerCase(); return x in UNITS ? UNITS[x] : null; }       // UNITS.get(x.lower())
  var UNIT_RX = '(?:' + Object.keys(UNITS).map(function (k, i) { return { k: k, i: i }; })
    .sort(function (a, b) { return (b.k.length - a.k.length) || (a.i - b.i); }).map(function (d) { return d.k; }).join('|') + ')';
  function ci(s) { return s.replace(/[a-z]/g, function (c) { return '[' + c + c.toUpperCase() + ']'; }); }   // case-insensitive group, spelled out letter by letter
  var GREET_RX = new RegExp('^(?:' + ci('hi|hello|hey|dear|good morning|good afternoon|good evening|morning|afternoon|greetings') +
    ")((\\s+[A-Z][" + WORDCH + ".'&-]*){0,4})?(\\s+(?:" + ci('all|team|there|everyone|folks') + "))?\\s*[,!.:]*\\s*$|^(?:" +
    ci('team|all|folks|everyone') + ")\\s*[,!:]\\s*$|^[A-Z][a-z]+( [A-Z][a-z]+)?\\s*,\\s*$|^(?:" +
    ci('thanks|thank you|thx|ty|tia|many thanks|thanks so much|thanks again|regards|best regards|kind regards|warm regards|best|sincerely|cheers|respectfully') +
    ")\\s*[,!.]*((\\s+[A-Z][" + WORDCH + ".'&-]*){0,3})?\\s*[,!.]*\\s*$");
  var NOISE_RX = /^((sub)?total\b[^\n]*[\d$]|grand total\b|(sales )?tax\b[^\n]*[\d$:]|(shipping|freight|handling)\b\s*[:$\d]|sent from my\b|ship to\b|bill to\b|attn\b|attention:|(po|p\.o\.|purchase order|order|invoice|inv|quote|req|requisition)\s*(#|no\.?|number|num)?\s*[:#]?\s*[A-Z0-9-]*\d[A-Z0-9-]*\s*$|(from|to|cc|bcc|subject|date|sent|re|fw|fwd)\s*:|(phone|ph|fax|tel|cell|mobile|email|e-mail)\s*[:#.]|page \d+( of \d+)?\s*$)/i;
  var CHATTER = mkset(['them', 'by', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'today', 'tomorrow', 'next', 'week',
    'possible', 'quote', 'shipping', 'asap', 'rush', 'urgent', 'when', 'is', 'are', 'be', 'have', 'has', 'do', 'does', 'it', 'thanks',
    'know', 'let', 'call', 'question', 'questions', 'price', 'pricing', 'invoice', 'account', 'attached', 'here', 'list', 'following',
    'below', 'above', 'same', 'double', 'usual', 'regular', 'hey', 'hi', 'see', 'sure', 'make', 'out', 'up', 'on', 'any', 'all']);
  var CONTACT_RX = /@|\b\d{3}[-. )]\s*\d{3}[-. ]\d{4}\b|\b(suite|ste|street|st\.?|avenue|ave|blvd|boulevard|road|rd\.?|drive|dr\.?|lane|ln\.?|pkwy|parkway|hwy|highway|way|ct|court|cir|circle|pl|place|po box|p\.o\. box)\b[^\n]*\b\d{5}\b|,\s*[A-Z]{2}\s+\d{5}(-\d{4})?\s*$|^[A-Za-z .]+\s+[A-Z]{2}\s+\d{5}(-\d{4})?(\s{2,}[^\n]*)?$|\bpage \d+ of \d+\b|(-{3,}\s*)?(forwarded|original) message|^begin forwarded/i;
  var HEADING_RX = /\b(par sheet|par list|checklist|check list|supply list|order list|restock list|requisition)\b|^(monthly|weekly|daily)\s+(restock|order|supply)|^(rig|engine|medic|truck|station|unit|ambulance|squad|ladder|rescue|battalion|ems unit)\s*#?\d+\b/i;
  var HEADING_M = new RegExp('^(?:' + HEADING_RX.source + ')', 'i');                              // HEADING_RX.match (anchored)
  var SECTIONS = mkset(['airway', 'airways', 'breathing', 'circulation', 'trauma', 'medications', 'meds', 'iv', 'iv supplies', 'ob', 'ob/gyn', 'peds', 'pediatric',
    'pediatrics', 'misc', 'miscellaneous', 'other', 'supplies', 'dressings', 'bleeding control', 'oxygen', 'o2', 'monitoring', 'diagnostics',
    'ppe', 'splinting', 'immobilization', 'bls', 'als', 'acls', 'pals', 'cardiac', 'respiratory', 'wound care', 'infection control',
    'training', 'equipment', 'disposables', 'consumables', 'jump bag', 'drug box', 'first in bag']);
  var LABELS = /(signed|signature|approved|authorized|checked by|received by|cartons?|weight|tracking|carrier|ship date|ship via|due date|valid|dept|department|cost center|cost centre|requested by|request by|ordered by|approved by|prepared by|submitted by|ship to|bill to|deliver to|ship via|terms|vendor|supplier|customer|cust|account|acct|customer id|po|po number|p\.o\.|order date|date needed|need by|delivery|location|contact|name|title|organization|agency|facility|phone|fax|email|address|notes?|comments?|reference|ref)/.source;
  var LABEL_RX = new RegExp('^' + LABELS + '\\s*(#|no\\.?)?\\s*:\\s*', 'i');
  var LEADER_RX = new RegExp('^(invoice|inv\\.?|customer id|cust\\.? id|account #|acct #|ref #|reference #)\\s*[#:]?\\s*[' + WORDCH + '-]*\\d', 'i');
  var QTY_LINE = new RegExp('^(?:qty|quantity|qty\\.)\\s*[:#]?\\s*(\\d{1,5})\\s*(' + UNIT_RX + ')?\\.?\\s*$', 'i');
  var QTY_SIGN = /(?:^|\s)(x\s*\d{1,5}(?![\d.])(?!\s*(?:in|ft|mm|cm|ga|g|fr)\b)|\d+\s*x\s)|\bqty\b|\(\s*\d{1,5}\s*\)|[,\t]\s*\d{1,5}\s*$|\s\d{1,4}\s*$/i;
  var QTY_MARK = /(?:^|\s)(x\s*\d{1,5}(?![\d.])|\d+\s*x\s)|\bqty\b|\(\s*\d{1,5}\s*\)/i;
  var SIZE_WORDS = mkset(['xs', 'xxs', 'small', 'sm', 'medium', 'med', 'large', 'lg', 'xl', 'xxl', 'adult', 'pediatric', 'peds', 'infant', 'neonatal', 'neonate', 'child', 'mini', 'regular', 'bariatric', 'standard', 'short', 'long', 'left', 'right']);
  var STOP = mkset(['for', 'with', 'and', 'the', 'of', 'a', 'an', 'in', 'to', 'by', 'box', 'case', 'pack', 'each', 'ea', 'bx', 'cs', 'qty', 'item', 'set', 'kit', 'per']);
  var PACK_UNIT = dict({ ea: 'EA', each: 'EA', bx: 'BX', box: 'BX', cs: 'CS', 'case': 'CS', pk: 'PK', pack: 'PK', pkg: 'PK' });
  var FILLER = mkset(['can', 'could', 'would', 'will', 'get', 'need', 'needs', 'want', 'please', 'send', 'ship', 'order', 'us', 'me', 'we', 'our',
    'you', 'your', 'i', 'the', 'of', 'a', 'an', 'some', 'more', 'also', 'and', 'for', 'to', 'on', 'in', 'at', 'with', 'from', 'this',
    'that', 'these', 'those', 'another', 'few', 'pls', 'thx', 'add', 'about', 'like', 'just', 'same', 'as', 'last', 'time', 'if']);

  function vocab_words(r, ix) {
    return words_of(/[a-z]{4,}/g, r.toLowerCase()).filter(function (w) { return ix.vocab.has(w.slice(0, 5)) && !FILLER.has(w) && !CHATTER.has(w); });
  }
  function strong_key_in(r, ix) {
    return r.split(/[\s,;\t|]+/).some(function (x) { return ix.is_key(x) && !short_num(norm(x)); });
  }
  function is_heading(raw, ix, in_block) {
    var r = rstripc(raw.trim(), ':').trim(), dw_, hw;
    if (strong_key_in(r, ix)) return false;
    if (!QTY_MARK.test(r)) {                                              // specific shapes first: "Ref 1608", "Order for Engine 7"
      if (/^(ref|reference)\b\s*#?\s*\d+\s*$/i.test(r)) return true;
      if (/^(order|orders|restock|supplies|request|requests|list|needs?)\s+(for|from)\b/i.test(r) && split_ws(r).length <= 7) return true;
      if (/\b(rig|engine|medic|truck|station|unit|ambulance|squad|ladder|rescue|battalion)\s*#?\d+\b/i.test(r) && split_ws(r).length <= 6 &&
          !vocab_words(r.replace(/\b(rig|engine|medic|truck|station|unit|ambulance|squad|ladder|rescue|battalion|restock|order|list|monthly|weekly)\b/gi, ''), ix).length) return true;
    }
    if (QTY_SIGN.test(raw.trim()) && !HEADING_M.test(r)) return false;
    if (HEADING_RX.test(r)) return true;
    if (SECTIONS.has(stripc(r.toLowerCase(), ' -'))) return true;                                // "AIRWAY", "Breathing"
    if (/:$/.test(raw.trim()) && split_ws(r).length <= 16) return true;                          // "Please send the following for our new medic unit:"
    if (/^(order|orders|restock|supplies|request|requests|list|needs?)\s+(for|from)\b/i.test(r) && split_ws(r).length <= 7) return true;
    if (/\b(rig|engine|medic|truck|station|unit|ambulance|squad|ladder|rescue|battalion)\s*#?\d+\b/i.test(r) && split_ws(r).length <= 6) return true;
    if (/^(ref|reference)\b\s*#?\s*\d+\s*$/i.test(r)) return true;                               // "Ref 1608"
    if (/^\([^\n]*\)$/.test(raw.trim())) return true;                                           // "(from Capt. Landry, 10/20)"
    dw_ = words_of(/[a-z]+/g, r.toLowerCase());
    if (dw_.length && dw_.length <= 4 && dw_.every(function (w) {
      return mkset(['order', 'orders', 'detail', 'details', 'summary', 'items', 'item', 'line', 'lines', 'supply', 'supplies',
        'request', 'requests', 'list', 'quote', 'quotation', 'invoice', 'packing', 'pick', 'picking', 'slip',
        'requisition', 'form', 'sheet', 'confirmation', 'estimate', 'page', 'continued', 'on', 'next', 'of']).has(w);
    }) && !QTY_MARK.test(r)) return true;                                                        // "Order Detail", "(continued on next page)"
    hw = words_of(/[a-z#]+/g, r.toLowerCase());
    if (hw.length && hw.length <= 5 && hw.every(function (w) {
      return mkset(['mfr', 'mfg', 'manufacturer', 'part', 'item', 'sku', 'catalog', 'cat', 'no', 'number', 'num', '#', 'description',
        'desc', 'qty', 'quantity', 'mpn', 'ref', 'uom', 'pack', 'price', 'list']).has(w);
    })) return true;                                                                             // "Mfr Part #"
    return false;
  }
  function is_noise(raw, ix) {
    var r0 = raw.trim(), words;
    if (/^on [^\n]{4,90} wrote:\s*$/i.test(r0)) return true;                                     // "On Thu, Oct 22 ... wrote:"
    if (strong_key_in(r0, ix)) return false;
    if (CONTACT_RX.test(r0)) return true;
    if (GREET_RX.test(r0)) return true;
    if (NOISE_RX.test(r0) || LEADER_RX.test(r0)) return true;
    if (LABEL_RX.test(r0) && !QTY_SIGN.test(r0)) return true;                                    // "Dept: ED", "Requested by: J. Hebert"
    if (/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/.test(r0) && split_ws(r0).length <= 7 && !vocab_words(r0, ix).length) return true;   // dated lines
    words = words_of(/[A-Za-z']+/g, r0.toLowerCase());
    if (words.length && !QTY_SIGN.test(r0) && !vocab_words(r0, ix).length) {
      if (/\?$/.test(r0)) return true;                                                           // "Can you quote and ship these?"
      if (words.length >= 4 && words.filter(function (w) { return FILLER.has(w) || CHATTER.has(w); }).length >= 0.8 * words.length) return true;
    }
    return false;
  }

  function pack_from_title(pt) {
    /* custom.order_form_pack_title: "ea" -> ["EA", 1], "10-bx" -> ["BX", 10]. */
    var m = /^\s*(?:(\d+)\s*[-\/ ]\s*)?([a-z]+)\s*$/.exec((pt || '').toLowerCase());
    if (!m || !(m[2] in PACK_UNIT)) return null;
    return [PACK_UNIT[m[2]], parseInt(m[1] || '1', 10)];
  }
  function pack_of(it) {
    var m = /(EA|BX|CS|PK)$/.exec(it.ns), t;
    if (m) return m[1];
    t = it.title.toLowerCase();
    if (/\b(case of|\/cs|per case|cases?\/|\bcs\b|\d+\s*(bx|boxes)\s*\/\s*(cs|case))/.test(t)) return 'CS';
    if (/\b(box of|\/box|\/bx|per box|\bbx\b)/.test(t)) return 'BX';
    if (/\b(pack of|\/pk|per pack)/.test(t)) return 'PK';
    return null;
  }

  // ------------------------------------------------------------------ the part number index
  function Index(rows) {
    var self = this, i, ks;
    this.items = [];
    this.byId = Object.create(null);
    rows.forEach(function (r) {
      var it = { id: r[0], h: r[1], sku: r[2] || '', mpn: r[3] || '', title: r[4] || '', price: r[5], vendor: r[9] || '', gated: r[6],
        kids: null, ns: norm(r[2]), nm: norm(r[3]), bc: lstrip0(norm(r[8])) };
      var pk = pack_from_title(r[10]);
      it.pack = pk ? pk[0] : pack_of(it);
      it.pack_n = pk ? pk[1] : null;
      self.items.push(it);
      self.byId[String(r[0])] = it;
    });
    this.byh = Object.create(null);
    this.items.forEach(function (it) { self.byh[it.h] = it; });
    this.key = Object.create(null);                 // SKU / MPN, exact after norm
    this.keys = [];                                 // the keys of ix.key in the order they first appeared
    this.z = Object.create(null);                   // all-digit keys without leading zeros (Excel)
    this.bc = Object.create(null);                  // barcode without leading zeros
    function put(d, k, it) { (d[k] || (d[k] = [])).push(it); }
    this.items.forEach(function (it) {
      ks = it.nm !== it.ns ? [it.ns, it.nm] : [it.ns];
      ks.forEach(function (k) {
        if (k) {
          if (!(k in self.key)) self.keys.push(k);
          put(self.key, k, it);
          if (isdigit(k)) put(self.z, lstrip0(k), it);
        }
      });
      if (it.bc) put(self.bc, it.bc, it);
    });
    this.items.forEach(function (it) {
      it.tw = mkset(words_of(/[a-z]{3,}/g, (it.title + ' ' + it.vendor).toLowerCase()).filter(function (w) { return !STOP.has(w); }).map(function (w) { return w.slice(0, 5); }));
      it.t2 = mkset(two_letter_words(it.title).map(function (w) { return w.toLowerCase(); }));
    });
    this.vocab = new Set();
    this.items.forEach(function (it) {
      words_of(/[a-z]{4,}/g, it.title.toLowerCase()).forEach(function (w) { self.vocab.add(w.slice(0, 5)); });
    });
    this.numtok = Object.create(null);              // digits found in SKU / MPN -> handles (first seen first)
    this.items.forEach(function (it) {
      [it.sku, it.mpn].forEach(function (code) {
        words_of(/\d+/g, code || '').forEach(function (tok) {
          var k = lstrip0(tok) || '0', l = self.numtok[k] || (self.numtok[k] = []);
          if (l.indexOf(it.h) < 0) l.push(it.h);
        });
      });
    });
    this.listing = Object.create(null);             // handle -> [size handles]; the parent itself is not buyable
    rows.forEach(function (r) {
      if (Array.isArray(r[7])) {
        var kids = [];
        r[7].forEach(function (id) { var k = self.byId[String(id)]; if (k) kids.push(k.h); });
        self.listing[r[1]] = kids;
        self.byId[String(r[0])].kids = kids;
      }
    });
    this.core = Object.create(null);                // SKU without pack suffix -> items (BX / CS siblings)
    this.items.forEach(function (it) { put(self.core, it.ns.replace(/(EA|BX|CS|PK|1EA)$/, ''), it); });
  }
  Index.prototype.is_key = function (tok, loose) {
    var n = norm(tok);
    if (n.length >= 3 && ((n in this.key) || (n in this.bc) || (isdigit(n) && (lstrip0(n) in this.bc)))) return true;
    // Excel drops leading zeros ("0022" -> "22"): only a fallback part number, never a quantity killer, never locked
    return !!loose && isdigit(n) && n.length >= 2 && (lstrip0(n) in this.z);
  };

  // ------------------------------------------------------------------ reading a pasted block
  function clean(s) {
    return replace_all(replace_all(s.replace(DASH_RX, '-'), '\u00a0', ' '), '\u00d7', 'x').trim();
  }
  function split_cells(raw) {
    var r = raw.trim(), s;
    if (r.charAt(0) === '|' || words_of(/\|/g, r).length >= 2) return stripc(r, '|').split('|').map(function (c) { return c.trim(); });
    if (raw.indexOf('\t') >= 0) return raw.split('\t').map(function (c) { return stripc(c.trim(), '"').trim(); });
    if (words_of(/\S {2,}(?=\S)/g, r).length >= 2 || (!/[,;]/.test(raw) && /\S {2,}\S/.test(r))) {
      return r.split(/ {2,}/).map(function (c) { return c.trim(); });                              // PDF columns; commas stay inside the description
    }
    s = raw.replace(/,(?=\d{3}(?!\d))/g, function (m0, off, str) { return off > 0 && /\d/.test(str.charAt(off - 1)) ? '' : m0; });   // 1,000 -> 1000
    if (/[,;]/.test(s)) return s.split(/[,;](?=(?:[^"]*"[^"]*")*[^"]*$)/).map(function (c) { return stripc(c.trim(), '"').trim(); });
    return [s.trim()];
  }
  var HDR = [['skip', /pack ?(qty|quantity|size)|case ?(qty|quantity)|units? ?per|per ?(box|case|pack)|inner|shipped|ship qty|received|back ?order|b\/o|on ?hand|^par\b|^min\b|^max\b|^stock|^avail|unit ?price|^price|^cost|^ext|^amount|^total/],
    ['line', /^line|^ln$|^no\.?$|^row|^#$/],
    ['part', /part|item ?(#|no|num)|sku|mpn|mfr|manufacturer|catalog|cat ?#|ref|product ?(code|#|no)|^code|^number/],
    ['desc', /desc|name|^item$|^product$|title/],
    ['qty', /^qty|quantity|^order|^ordered|^need|to order|reorder|count|requested/],
    ['unit', /^uom|^unit$|^u\/m/]];
  var QTY_PRIORITY = [/order|need|to order|reorder|requested/, /^qty$|^quantity$/, /[^\n]/];
  function header_map(cells, ix) {
    var m = {}, qtys = [], n, p, hit;
    if (cells.some(function (c) { return split_ws(c).length > 4 || /\d{2,}/.test(c) || (ix && ix.is_key(c)) || c.indexOf(':') >= 0; })) return null;
    cells.forEach(function (c, i) {
      var cl = c.toLowerCase().trim(), j, k;
      for (j = 0; j < HDR.length; j++) {
        k = HDR[j][0];
        if (HDR[j][1].test(cl)) {
          if (k === 'qty') qtys.push([i, cl]);
          else if (!(k in m)) m[k] = i;
          break;
        }
      }
    });
    for (p = 0; p < QTY_PRIORITY.length; p++) {                       // "Qty Ordered" over "Qty"; never "Qty Shipped"
      hit = qtys.filter(function (q) { return QTY_PRIORITY[p].test(q[1]); });
      if (hit.length) { m.qty = hit[0][0]; break; }
    }
    delete m.skip;
    n = Object.keys(m).length;
    return (('part' in m) || ('desc' in m)) && n >= 2 ? m : null;
  }
  var QTY_CELL = new RegExp('^(?:(\\d{1,5})(?:\\.0+)?\\s*(' + UNIT_RX + ')?\\.?)$', 'i');
  var PRICE_CELL = /^(?:\$?\s*\d+\.\d{2}|\$\s*\d+)$/;
  function cell_kind(c, ix) {
    c = c.trim();
    if (!c) return 'empty';
    if (PRICE_CELL.test(c)) return 'price';
    if (ix.is_key(c)) return 'key';
    if (QTY_CELL.test(c)) return 'qty';
    if (words_of(/[A-Za-z]{2,}/g, c).length >= 2) return 'desc';
    if (/\d/.test(c) && /[A-Za-z]/.test(c) && split_ws(c).length <= 2) return 'partish';
    return 'other';
  }
  function infer_columns(rows, ix) {
    var ncol = Math.max.apply(null, rows.map(function (r) { return r.length; })), roles = {}, stats = [], i, n = rows.length;
    function taken(idx) { return ('line' in roles && roles.line === idx) || ('part' in roles && roles.part === idx) || ('qty' in roles && roles.qty === idx) || ('desc' in roles && roles.desc === idx); }
    function kcount(k, name) { return k[name] || 0; }
    for (i = 0; i < ncol; i++) {
      (function (i) {
        var kinds = {}, vals, nums, steps, seq, j;
        rows.forEach(function (r) { var kd = i < r.length ? cell_kind(r[i], ix) : 'empty'; kinds[kd] = (kinds[kd] || 0) + 1; });
        vals = rows.filter(function (r) { return i < r.length; }).map(function (r) { return r[i].trim(); });
        nums = vals.filter(isdigit).map(function (v) { return parseInt(v, 10); });
        steps = [];
        for (j = 0; j + 1 < nums.length; j++) { if (steps.indexOf(nums[j + 1] - nums[j]) < 0) steps.push(nums[j + 1] - nums[j]); }
        seq = (vals.filter(function (v, k) { return isdigit(v) && parseInt(v, 10) === k + 1; }).length >= Math.max(2, vals.length * 0.8)) ||
          (nums.length >= 2 && nums.length >= vals.length * 0.8 && steps.length === 1 && steps[0] > 0);      // 1,2,3 or 10,20,30
        stats.push([i, kinds, seq]);
      })(i);
    }
    stats.forEach(function (s) { if (s[2] && !('line' in roles)) roles.line = s[0]; });
    sorted_by(stats, function (s) { return -(kcount(s[1], 'key') + kcount(s[1], 'partish')); }).some(function (s) {
      if (s[0] !== roles.line && (kcount(s[1], 'key') + kcount(s[1], 'partish')) >= n * 0.5) { roles.part = s[0]; return true; }
      return false;
    });
    sorted_by(stats, function (s) { return -kcount(s[1], 'qty'); }).some(function (s) {
      if (!taken(s[0]) && kcount(s[1], 'qty') >= n * 0.5 && kcount(s[1], 'price') === 0) { roles.qty = s[0]; return true; }
      return false;
    });
    sorted_by(stats, function (s) { return -kcount(s[1], 'desc'); }).some(function (s) {
      if (!taken(s[0]) && kcount(s[1], 'desc') >= n * 0.5) { roles.desc = s[0]; return true; }
      return false;
    });
    roles.price = stats.filter(function (s) { return kcount(s[1], 'price') >= n * 0.5; }).map(function (s) { return s[0]; });
    return (('part' in roles) || ('desc' in roles)) ? roles : null;
  }

  var CONNECT = mkset(['and', '&', '+', '/', '|', 'also', 'then']);
  var ATTR_WORDS = mkset(['xs', 'xxs', 'small', 'sm', 'medium', 'med', 'large', 'lg', 'xl', 'xxl', 'adult', 'pediatric', 'peds', 'infant', 'neonatal', 'neonate', 'child', 'mini', 'regular', 'bariatric', 'standard', 'short', 'long', 'left', 'right',
    'size', 'sz', 'box', 'boxes', 'case', 'cases', 'pack', 'packs', 'of', 'qty', 'each', 'ea', 'bx', 'cs', 'pk', 'sterile',
    'non', 'nonsterile', 'latex', 'free', 'color', 'colour', 'black', 'grey', 'gray', 'blue', 'red', 'green', 'white', 'yellow',
    'pink', 'purple', 'orange', 'clear', 'per', 'plus', 'single', 'use', 'disposable', 'reusable', 'rush']);
  function short_num(k) { return isdigit(k) && k.length <= 4; }
  function key_spans(toks, ix) {
    /* Longest exact part numbers first. Tokens after the first must contain a letter, so a quantity is never
       glued onto a part number ("2030 9" stays 2030 + 9; "SR-SFP-NCR-36N OXI" is one SKU). */
    var spans = [], i = 0, hit, j, seg;
    while (i < toks.length) {
      hit = null;
      if (!/[A-Za-z0-9]/.test(toks[i]) || CONNECT.has(toks[i].toLowerCase())) { i += 1; continue; }     // "/", "&", "and" never start a part number
      for (j = Math.min(toks.length, i + 5); j > i; j--) {
        seg = toks.slice(i, j);
        if (j - i > 1) {
          if (!/[A-Za-z]/.test(seg[seg.length - 1]) || seg.slice(1).some(function (x) { return CONNECT.has(x.toLowerCase()) || !/[A-Za-z0-9]/.test(x); })) continue;
          if (!seg.slice(1).every(function (x) { return /[A-Za-z]/.test(x) && !(x.toLowerCase() in UNITS); }) && !(norm(seg.join(' ')) in ix.key)) continue;
        }
        if (ix.is_key(seg.join(' '))) { hit = [i, j]; break; }
      }
      if (hit) { spans.push(hit); i = hit[1]; } else i += 1;
    }
    return spans;
  }
  var MEASURE = '(?:ft|foot|feet|in|inch|inches|mm|cm|m|ml|l|g|gm|kg|lb|lbs|oz|fr|french|ga|gauge|mg|mcg|cc|yd|yds|%|")';
  var MEASURE_FM = new RegExp('^(?:' + MEASURE + ')$', 'i');
  var MEASURE_SUB = new RegExp('(\\d+(?:\\.\\d+)?)\\s*(' + MEASURE + ')(?![A-Za-z])', 'gi');
  function covered_of(spans) {
    var c = {}, k, a, b;
    for (k = 0; k < spans.length; k++) for (a = spans[k][0], b = spans[k][1]; a < b; a++) c[a] = 1;
    return c;
  }
  var SR_QTY_ONLY = new RegExp('^(?:\\d{1,5}\\s*(?:' + UNIT_RX + ')?\\s*(?:of\\s+the|of)?\\s*)$', 'i');
  var SR_ITEM = new RegExp('^(?:(\\d{1,5}|a|an|one|two|three|four|five|six|ten|twelve)\\s+)?(?:(' + UNIT_RX + ')\\s+)?(?:of\\s+(?:the\\s+)?)?([^\\n]*)$', 'i');
  function sentence_rest(toks, spans, ix) {
    /* Phrases of a sentence that hold no part number but name an item: "... and a case of gloves large". */
    var covered = covered_of(spans), out = [];
    var text = toks.map(function (x, i) { return !(i in covered) ? x : '|'; }).join(' ');
    text.split(/\||\b(?:and|also|plus)\b|[,;]/i).forEach(function (seg) {
      var m, rest, q, qty;
      seg = seg.trim();
      if (SR_QTY_ONLY.test(seg)) return;                                                         // the quantity of a part number
      m = SR_ITEM.exec(seg);
      if (!m) return;
      rest = stripc(m[3], ' .!?');
      if (!vocab_words(rest, ix).length) return;
      q = m[1];
      qty = q && isdigit(q) ? parseInt(q, 10) : numword((q || '').toLowerCase());
      if (qty === null && !m[2]) return;                                                         // needs "2", "a", or a pack word to be an item
      out.push({ parts: [], desc: rest, text: rest, qty: qty || 1, src: q ? 'explicit' : 'default', unit: unit_of(m[2]),
        extra: [], whole: null, short: false, split: true, sentence: true });
    });
    return out;
  }
  var LINK_SRC = 'https?://[^\\s]*?/products/([a-z0-9][a-z0-9\\-%._]*)';
  var LINK_ONE = new RegExp(LINK_SRC, 'i');
  var NUMWORD_UNIT = new RegExp('\\b(' + Object.keys(NUMWORD).join('|') + ')\\s+(?=(' + UNIT_RX + ')\\b)', 'gi');
  function read_free(text, ix, depth) {
    depth = depth || 0;
    /* One cell of free text -> a LIST of items (two part numbers on one line are two items). */
    var t = clean(text), links, out, cuts, segs, k, m, rx, i;
    var toks, spans, strong, qty_is_key, prods, a_, b_, n, hs, covered, rest, bounds, nxt, seg, sub, q, src, mq, part, cut, head, first, item;
    rx = new RegExp(LINK_SRC, 'gi'); links = [];
    while ((m = rx.exec(t))) links.push(m[1]);
    if (links.length) {                                          // a pasted product link names the product exactly
      out = []; cuts = []; rx = /https?:\/\//g;
      while ((m = rx.exec(t))) cuts.push(m.index);               // re.split(r"(?=https?://)", t)
      segs = []; k = 0;
      for (i = 0; i < cuts.length; i++) { if (cuts[i] > k) segs.push(t.slice(k, cuts[i])); k = cuts[i]; }
      segs.push(t.slice(k));
      segs.forEach(function (sg) {
        var lm = LINK_ONE.exec(sg), rest_, lq, h;
        if (!lm) return;
        rest_ = sg.slice(lm.index + lm[0].length).replace(/^\S*/, '');
        lq = /(?:^|\s)(?:[x\u00d7]\s*|-\s*|qty:?\s*)?(\d{1,5})\b/.exec(rest_);
        h = lm[1].split('?')[0].toLowerCase();
        out.push({ parts: [], link: h, desc: '', text: replace_all(h, '-', ' '), qty: lq ? parseInt(lq[1], 10) : 1,
          src: lq ? 'explicit' : 'default', unit: null, extra: [], whole: null, short: false });
      });
      return out;
    }
    t = t.replace(MEASURE_SUB, function (m0, num, unit, off, str) {                              // not glued to a word or a dot before the number: checked by hand
      if (off > 0 && (str.charAt(off - 1) === '.' || WORD_RX.test(str.charAt(off - 1)))) return m0;
      return num + replace_all(replace_all(replace_all(replace_all(replace_all(unit.toLowerCase(), 'foot', 'ft'), 'feet', 'ft'), 'inches', 'in'), 'inch', 'in'), '"', 'in');
    });
    t = t.replace(/(\d{1,5})\s*@\s*\$?\s*[\d,]+(?:\.\d{1,2})?/g, '$1');                          // "3 @ $34.00" -> 3
    t = t.replace(/\$\s*[\d,]+(?:\.\d{2})?(?!\S)/g, function (m0, off, str) {                    // stray prices, only after whitespace or at the start
      return off > 0 && !/\s/.test(str.charAt(off - 1)) ? m0 : ' ';
    });
    t = t.replace(/^\s*(\d{1,3}[.)]\s+|[-*\u2022]\s+)/, '');
    t = t.replace(/^(p\.?\s?s\.?|ps)[:.,]?\s+/i, '');
    t = t.replace(/^(item|part|part ?#|part no\.?|sku|mfr|cat(alog)?( ?#)?)\s*[:#]\s*/i, '');
    m = /^([A-Za-z][A-Za-z \/&]{1,24}):\s+(?=\S)/.exec(t);
    if (m && !LABEL_RX.test(t) && split_ws(m[1]).length <= 3 && !ix.is_key(m[1])) {
      t = t.slice(m[0].length);                                                                  // "Airway: OPA size 4 x2" -> "OPA size 4 x2"
    }
    t = t.replace(/\bhalf\s+a\s+dozen\b/gi, '6 convunits');
    t = t.replace(/\b(\d{1,4}|a|an|one|two|three|four|five|six|ten|twelve)\s+(dozen|doz|dz|gross)\b/gi, function (m0, a, b) {
      return String((isdigit(a) ? parseInt(a, 10) : NUMWORD[a.toLowerCase()]) * (b.toLowerCase() === 'gross' ? 144 : 12)) + ' convunits';
    });
    t = t.replace(NUMWORD_UNIT, function (m0, a) {
      return String(NUMWORD[a.toLowerCase()]) + ' ';
    });
    m = /\s*[|\/]\s*(\d+)\s*$/.exec(t);                                                          // only after a word character: checked by hand
    if (m && m.index > 0 && WORD_RX.test(t.charAt(m.index - 1)) && !ix.is_key(m[1])) t = t.slice(0, m.index) + ' ' + m[1];   // "SKU/5", "SKU|5" -> "SKU 5" (not "2508 / 2509")
    toks = split_ws(t);
    spans = key_spans(toks, ix);
    // a short all-digit part number next to a real one is the quantity ("350 AL-1033.C-EA")
    strong = spans.filter(function (sp) { return !short_num(norm(toks.slice(sp[0], sp[1]).join(' '))); });
    qty_is_key = false;
    if (strong.length && spans.length > strong.length) {
      qty_is_key = true;
      spans = strong;
    }
    // distinct products among the spans
    prods = [];
    spans.forEach(function (sp) {
      var nn = norm(toks.slice(sp[0], sp[1]).join(' ')), hh = products_of(nn, ix);
      if (!prods.some(function (p) { return same_tuple(p[0], hh); })) prods.push([hh, sp]);
    });
    if (prods.length >= 2) {
      covered = covered_of(spans);
      rest = toks.filter(function (x, i) { return !(i in covered); });
      if (prods.length === 2 && JSON.stringify(rest.filter(function (x) { return !/^(?:[x\u00d7]?\d{1,5}[x\u00d7]?|\(\d{1,5}\))$/i.test(x); })) === '["/"]') {
        item = _one(t, toks, spans.slice(0, 1), ix);                // "A / B": one or both? the buyer says
        item.parts = prods.map(function (p) { return toks.slice(p[1][0], p[1][1]).join(' '); });
        item.slash_pair = prods.map(function (p) { return p[0]; });
        return [item];
      }
      var listy = rest.every(function (x) {
        return /^(?:[x\u00d7]?\d{1,5}[x\u00d7]?|\(\d{1,5}\)|qty:?)$/i.test(x) || (x.toLowerCase() in UNITS) || CONNECT.has(x.toLowerCase()) || x === ',' || x === ';' || x === '-';
      });
      if (listy) {                                                  // "A x2 B x5", "A and B", "A / B": split
        out = [];
        bounds = prods.map(function (p) { return p[1]; }).concat([[toks.length, toks.length]]);
        for (k = 0; k < prods.length; k++) {
          a_ = bounds[k][0]; b_ = bounds[k][1]; nxt = bounds[k + 1][0];
          seg = toks.slice(a_, nxt).join(' ');
          sub = read_free(seg, ix, depth + 1)[0]; sub.split = true; out.push(sub);
        }
        return out;
      }
      // a sentence ("3 of the A and 2 of the B ... also 1 C"): one item per part number, quantity from next to it
      out = [];
      prods.forEach(function (p) {
        var aa = p[1][0], bb = p[1][1], qq = null, ss = 'default', mm = aa >= 1 ? /^(\d{1,5})$/.exec(toks[aa - 1]) : null, pt;
        if (aa >= 3 && toks[aa - 2].toLowerCase() === 'of' && /^\d{1,5}$/.test(toks[aa - 3])) qq = parseInt(toks[aa - 3], 10);
        else if (aa >= 2 && toks[aa - 1].toLowerCase() === 'of' && /^\d{1,5}$/.test(toks[aa - 2])) qq = parseInt(toks[aa - 2], 10);
        else if (mm) qq = parseInt(mm[1], 10);
        else if (bb < toks.length && /^(?:[x\u00d7](\d{1,5})|\((\d{1,5})\))$/i.test(toks[bb])) qq = parseInt(toks[bb].replace(/\D/g, ''), 10);
        else if (bb + 1 < toks.length && (toks[bb].toLowerCase() === 'x' || toks[bb].toLowerCase() === '\u00d7') && /^\d{1,5}$/.test(toks[bb + 1])) qq = parseInt(toks[bb + 1], 10);
        if (qq !== null) ss = 'explicit';
        pt = toks.slice(aa, bb).join(' ');
        out.push({ parts: [pt], desc: '', text: pt, qty: qq !== null ? qq : 1, src: ss, unit: null, extra: [], whole: null,
          short: short_num(norm(pt)), split: true, sentence: true });
      });
      return out.concat(sentence_rest(toks, prods.map(function (p) { return p[1]; }), ix));
    }
    if (prods.length === 1 && depth === 0 && /\b(and|also|plus)\b/i.test(toks.join(' '))) {   // once: no endless re-reading
      rest = sentence_rest(toks, [prods[0][1]], ix);
      if (rest.length) {                                            // "2 boxes of A and a case of gloves large"
        b_ = prods[0][1][1];
        cut = /\b(and|plus)\b/i.exec(toks.slice(b_).join(' '));
        head = toks.slice(0, b_).join(' ') + (cut ? ' ' + toks.slice(b_).join(' ').split(cut[0])[0] : '');
        first = read_free(head.trim().replace(/^(also|and|plus)\s+/i, ''), ix, depth + 1)[0];
        first.split = true;
        return [first].concat(rest);
      }
    }
    item = _one(t, toks, spans, ix);
    if (toks.filter(function (x) { return /^[A-Za-z]{2,}$/.test(x) && FILLER.has(x.toLowerCase()); }).length >= 3) item.sentence = true;
    if (qty_is_key) item.qty_is_key = true;
    if (prods.length >= 2) item.many_keys = prods.map(function (p) { return p[0]; });
    return [item];
  }
  var ONE_PATS = [new RegExp('\\bqty\\.?\\s*:?\\s*(\\d{1,5})\\b\\s*(' + UNIT_RX + ')?', 'i'),
    new RegExp('\\(\\s*(\\d{1,5})\\s*(' + UNIT_RX + ')?\\s*\\)', 'i'),
    /(?:^|\s)[x\u00d7]\s*(\d{1,5})(?=\s|$)/i,
    /(?:^|\s)(\d{1,5})\s*[x\u00d7](?=\s|$)/i,
    new RegExp('(?:^|\\s)(\\d{1,5})\\s*(' + UNIT_RX + ')\\.?(?=\\s|$)', 'i'),
    /\s-\s*(\d{1,5})\s*$/i];
  var ONE_UNIT = new RegExp('(?:^|\\s)(' + UNIT_RX + ')\\.?(?=\\s|$)', 'i');
  function _one(t, toks, spans, ix) {
    var qty = null, unit = null, src = 'default', covered = covered_of(spans), free, pats, i, m, mu, ftoks, rest, named_num, nums, extra, parts, desc, text, whole;
    free = toks.map(function (x, i) { return !(i in covered) ? x : '\x00'; }).join(' ');
    pats = ONE_PATS;
    for (i = 0; i < pats.length; i++) {
      m = pats[i].exec(free);
      if (m) {
        qty = parseInt(m[1], 10); src = 'explicit';
        if (m[2]) unit = unit_of(m[2]);
        free = free.slice(0, m.index) + ' ' + free.slice(m.index + m[0].length); break;
      }
    }
    mu = ONE_UNIT.exec(free);
    if (mu && !unit) { unit = unit_of(mu[1]); free = free.slice(0, mu.index) + ' ' + free.slice(mu.index + mu[0].length); }
    ftoks = split_ws(free);
    rest = ftoks.filter(function (x) { return x !== '\x00'; });
    function after_word(i) {
      var p = i > 0 ? ftoks[i - 1] : null;
      return i > 0 && /^[A-Za-z][A-Za-z0-9.\-]*$/.test(p) && !(p.toLowerCase() in UNITS) && !CONNECT.has(p.toLowerCase()) &&
        !FILLER.has(p.toLowerCase()) && p.toLowerCase() !== 'qty' && p.toLowerCase() !== 'x';
    }
    named_num = {};
    ftoks.forEach(function (x, i) { if (/^\d+\.\d+$/.test(x) || (/^\d{1,5}$/.test(x) && after_word(i))) named_num[i] = 1; });
    nums = ftoks.filter(function (x, i) { return /^\d{1,5}$/.test(x) && !(i in named_num); });
    if (qty === null && nums.length) {
      qty = parseInt(nums[0], 10); src = 'bare'; nums = nums.slice(1);
      if (rest.indexOf(String(qty)) >= 0) rest.splice(rest.indexOf(String(qty)), 1);
    }
    extra = nums;
    parts = spans.map(function (sp) { return toks.slice(sp[0], sp[1]).join(' '); });
    if (!parts.length) {
      parts = rest.filter(function (x) { return (/\d/.test(x) && /[A-Za-z]/.test(x)) || /^\d{4,}$/.test(x) || /^[A-Za-z0-9]+(?:[-.\/][A-Za-z0-9]+)+$/.test(x); });
      if (!parts.length) {
        var z = rest.filter(function (x) { return ix.is_key(x, true); });
        if (z.length) parts = z.slice(0, 1);
      }
    }
    desc = stripc(ftoks.filter(function (x, i) {
      return x !== '\x00' && parts.indexOf(x) < 0 && extra.indexOf(x) < 0 && (/[A-Za-z]/.test(x) || (i in named_num));
    }).join(' '), ' -:');
    text = toks.filter(function (x) { return extra.indexOf(x) < 0 && !(src === 'bare' && x === String(qty)); }).join(' ').trim();
    whole = null;
    if (!spans.length && toks.length >= 2 && toks.length <= 4 && toks.some(function (x) { return /\d{3,}|\d[A-Za-z]|[A-Za-z]\d/.test(x); })) {
      whole = norm(t);                                                                           // "io 742 en": offered, never locked
    }
    return { parts: parts, desc: desc, text: text, qty: qty !== null ? qty : 1, src: src, unit: unit, extra: extra, whole: whole,
      short: parts.length > 0 && parts.every(function (p) { return short_num(norm(p)); }) };
  }

  function products_of(tok, ix) {
    var n = norm(tok);
    return (ix.key[n] || ix.bc[lstrip0(n)] || []).map(function (it) { return it.h; }).sort();
  }
  var COLORS = mkset(['black', 'grey', 'gray', 'blue', 'red', 'green', 'white', 'yellow', 'pink', 'purple', 'orange', 'clear', 'tan', 'brown', 'navy']);
  var QSTART_PREV = mkset(['and', 'also', 'plus', 'need', 'needs', 'get', 'add', 'send', 'want', '&', '+', 'with', 'then']);
  function clause_split(t, ix) {
    /* 'need 4 pedi nrb masks and 2 of the adult stethoscopes' -> two clauses. A quantity starts a clause only at the
       start, after punctuation, or after and/also/plus/need/get/add/send; a number after a word ("masimo 2317") does not. */
    var toks = split_ws(t), starts = [], i, x, xl, isnum, prev, cuts, out = [];
    for (i = 0; i < toks.length; i++) {
      x = toks[i];
      xl = stripc(x.toLowerCase(), ',.;:');
      isnum = (/^\d{1,5}$/.test(xl) && !ix.is_key(xl)) ||
        ((xl in NUMWORD) && (xl !== 'a' && xl !== 'an' || (i + 1 < toks.length && (toks[i + 1].toLowerCase() in UNITS))));
      if (!isnum || i + 1 >= toks.length || !/[A-Za-z]/.test(toks[i + 1])) continue;
      prev = i ? toks[i - 1] : '';
      if (i === 0 || /[,;:.!?]$/.test(prev) || QSTART_PREV.has(prev.toLowerCase())) starts.push(i);
    }
    if (starts.length < 2) return null;
    cuts = ((starts[0] ? [0] : []).concat(starts)).filter(function (v, k, a) { return a.indexOf(v) === k; }).sort(function (a, b) { return a - b; });
    cuts.forEach(function (a_, k) {
      var b_ = k + 1 < cuts.length ? cuts[k + 1] : toks.length, seg = toks.slice(a_, b_);
      while (seg.length && QSTART_PREV.has(stripc(seg[seg.length - 1].toLowerCase(), ',.;'))) seg = seg.slice(0, -1);   // "... masks and" -> "... masks"
      out.push(stripc(seg.join(' '), ' ,;.'));
    });
    return out.filter(Boolean);
  }
  function resolve_ellipsis(items) {
    /* '3 onyx pulse ox red and 2 blue': the second item is the same product in blue. */
    var prev = null;
    items.forEach(function (it) {
      var words, base;
      if (it.noise) return;
      words = words_of(/[A-Za-z]+/g, it.desc == null ? '' : it.desc).map(function (w) { return w.toLowerCase(); });
      if (prev && !(it.parts && it.parts.length) && words.length && words.every(function (w) { return COLORS.has(w) || SIZE_WORDS.has(w); })) {
        base = split_ws(prev.desc).filter(function (w) { return !COLORS.has(w.toLowerCase()) && !SIZE_WORDS.has(w.toLowerCase()); });
        it.desc = it.text = base.concat(split_ws(it.desc)).join(' ');
        it.ellipsis = true;
      } else if (!(it.parts && it.parts.length)) prev = it;
      else prev = null;
    });
    return items;
  }
  var PROSE_END = /[.!?]\s*$/;
  function is_prose(raw, ix) {
    var r = raw.trim(), words = words_of(/[A-Za-z']+/g, r);
    if (strong_key_in(r, ix) || QTY_MARK.test(r) || raw.indexOf('\t') >= 0 || split_cells(raw).length >= 3) return false;
    if (r.split(/[\s,;|]+/).some(function (x) { return ix.is_key(x) || ix.is_key(x, true); })) return false;
    if (words.length >= 10) return true;                                                         // a paragraph, not an order line
    return words.length >= 5 && PROSE_END.test(r) && vocab_words(r, ix).length <= 1;
  }
  function last_real(out) {                                  // next((o for o in reversed(out) if not o.get("noise")), None)
    var i;
    for (i = out.length - 1; i >= 0; i--) if (!out[i].noise) return out[i];
    return null;
  }
  function read_block(text, ix) {
    var lines = text.split('\n').map(function (l) { return clean(l).replace(/^(\s*>)+\s?/, ''); });
    lines = lines.filter(function (l) { return l.trim() && !/^[\s|:\-=+_*~.]+$/.test(l); });   // table rules and separators
    var out = [], in_block, rows, hm = null, hi, q, widths, order, signed, li, raw, cells;
    if (!lines.length) return out;
    in_block = lines.length >= 3;
    rows = lines.map(function (l) { return split_cells(l); });
    for (hi = 0; hi < Math.min(8, rows.length); hi++) {      // a header row may sit under title, address or label lines
      if (hi && strong_key_in(lines[hi - 1], ix)) break;     // items have started: no header above them
      hm = header_map(rows[hi], ix);
      if (hm) {
        for (q = 0; q < hi; q++) out.push({ raw: lines[q], noise: true });
        rows = rows.slice(hi + 1); lines = lines.slice(hi + 1); break;
      }
    }
    if (!hm && rows.length >= 2) {
      widths = {}; order = [];
      rows.forEach(function (r) { var w = r.filter(Boolean).length; if (!(w in widths)) { widths[w] = 0; order.push(w); } widths[w]++; });
      var bw = order[0];
      order.forEach(function (w) { if (widths[w] > widths[bw]) bw = w; });                       // Counter.most_common(1): first of the largest
      if (bw >= 2 && widths[bw] >= rows.length * 0.8) {      // a regular table: work the columns out from all rows
        hm = infer_columns(rows.filter(function (r) { return r.filter(Boolean).length === bw; }), ix);
      }
    }
    signed = false;
    for (li = 0; li < lines.length; li++) {
      raw = lines[li]; cells = rows[li];
      var keyed_line = raw.split(/[\s,;\t|]+/).some(function (x) { return ix.is_key(x); });
      var has_qty = /([,\t]\s*\d{1,5}\s*$|(?:^|\s)x\s*\d|\d+\s*x\s|\(\s*\d{1,5}\s*\)|\bqty|\d{4,}|[A-Za-z]\d|\d[A-Za-z])/i.test(raw);
      if (signed && !keyed_line && !has_qty && !vocab_words(raw, ix).length) {
        out.push({ raw: raw, noise: true, signature: true }); continue;                        // under "Thanks," after the items
      }
      if (/^(thanks|thank you|thx|regards|best regards|sincerely|cheers|sent from)\b/i.test(raw.trim()) && !keyed_line &&
          out.some(function (o) { return !o.noise; })) signed = true;
      var m = QTY_LINE.exec(raw.trim());
      if (m) {                                               // "Qty: 3" under its item
        var prev = last_real(out);
        if (prev && prev.src === 'default') {
          prev.qty = parseInt(m[1], 10); prev.src = 'explicit';
          prev.unit = prev.unit || unit_of(m[2]); continue;
        }
      }
      var prev_item = last_real(out);
      var prev_tab = prev_item !== null && (split_cells(prev_item.raw).length >= 3 || /,$/.test(prev_item.raw.replace(/\s+$/, '')));
      var same_key = prev_item !== null && !!(prev_item.parts && prev_item.parts.length) && raw.split(/[\s,;]+/).some(function (x) {
        var px;
        if (!ix.is_key(x)) return false;
        px = products_of(x, ix);
        return px.length > 0 && same_tuple(px, products_of(prev_item.parts[0], ix));
      });
      var bare_nums = raw.split(/[\s,;|()]+/).filter(function (x) { return /^\d{1,4}$/.test(x) && !ix.is_key(x); });
      var no_item_signs = (same_key || !raw.split(/[\s,;\t|]+/).some(function (x) { return ix.is_key(x) || ix.is_key(x, true); })) &&
        !cells.some(function (c) { return c && QTY_CELL.test(c.trim()); }) && !QTY_SIGN.test(raw) &&
        !/^\s*([-*\u2022]|\d{1,3}[.)]|\d+\s)/.test(raw) && !/\(\s*\d/.test(raw) && !(same_key && bare_nums.length);   // its own quantity: an item, not a wrapped line
      if ((hm || prev_tab) && cells.filter(Boolean).length === 1 && no_item_signs && !header_map(cells, ix) &&
          (out.length && (!out[out.length - 1].noise || out[out.length - 1].continued))) {
        prev_item.desc = ((prev_item.desc == null ? '' : prev_item.desc) + ' ' + raw.trim()).trim();   // a wrapped description line
        out.push({ raw: raw, noise: true, continued: true }); continue;
      }
      if (header_map(cells, ix) && cells.filter(Boolean).length >= 2) {
        out.push({ raw: raw, noise: true }); continue;                                         // a header repeated on page 2
      }
      // (the Python has a dead "if False and hm ..." wrapped-line branch here; nothing to port)
      if (hm === null && raw.indexOf('\t') < 0) {
        var cl = clause_split(raw, ix);
        if (cl && cl.length) {                                                                 // a sentence holding several items
          var got = [];
          cl.forEach(function (c) {
            if (is_noise(c, ix) || (!vocab_words(c, ix).length && !strong_key_in(c, ix) && !split_ws(c).some(function (x) { return ix.is_key(x); }))) {
              got.push({ raw: raw, noise: true, clause: c }); return;
            }
            read_free(c, ix).forEach(function (r_) { r_.raw = raw; r_.split = true; got.push(r_); });
          });
          Array.prototype.push.apply(out, resolve_ellipsis(got)); continue;
        }
      }
      if (is_noise(raw, ix) || is_heading(raw, ix, in_block) || is_prose(raw, ix)) {
        out.push({ raw: raw, noise: true }); continue;
      }
      var two_items = false;
      if (raw.indexOf('\t') < 0) {
        var tset = Object.create(null);
        cells.forEach(function (c) {
          split_ws(clean(c)).forEach(function (x) { if (ix.is_key(x) && !short_num(norm(x))) tset['T' + products_of(x, ix).join('\u0000')] = 1; });
        });
        cells.forEach(function (c) {
          if (/(?:^|\s)(x\s*\d|\d+\s*x\b|\(\d+\))/i.test(c) && ix.is_key(split_ws(clean(c))[0])) tset['C' + c] = 1;
        });
        two_items = Object.keys(tset).length >= 2;
      }
      if (hm && cells.filter(Boolean).length >= 2 && !two_items) {
        var g = function (k) { return (k in hm) && typeof hm[k] === 'number' && hm[k] < cells.length ? cells[hm[k]].trim() : ''; };
        var it = { raw: raw, parts: g('part') ? [g('part')] : [], desc: g('desc'), text: g('desc') || g('part'), qty: 1, src: 'default', unit: null, extra: [], whole: null };
        if (!it.parts.length && g('desc') && ix.is_key(g('desc'))) { it.parts = [g('desc')]; it.desc = ''; }      // "Item" column holding SKUs
        it.short = it.parts.length > 0 && it.parts.every(function (p) { return short_num(norm(p)); });
        var mq = g('qty') ? QTY_CELL.exec(g('qty')) : null;
        if (mq) { it.qty = parseInt(mq[1], 10); it.src = 'explicit'; it.unit = unit_of(mq[2]); }
        if (g('unit')) { var uu = stripc(g('unit').toLowerCase(), '.'); it.unit = uu in UNITS ? UNITS[uu] : it.unit; }
        if (it.parts.length && !ix.is_key(it.parts[0]) && !ix.is_key(it.parts[0], true)) {
          var f = read_free(it.parts[0], ix)[0];                    // the part cell may hold "SKU x2" etc.
          if (f.parts.length) it.parts = f.parts;
        }
        if (!it.parts.length && !it.desc) {
          var f2 = read_free(raw, ix)[0]; f2.raw = raw; it = f2;
        }
        out.push(it); continue;
      }
      var cs = cells.filter(Boolean);
      if (cs.length >= 2) {
        var kv = [];
        cs.forEach(function (c) {
          var mk = /^(sku|part ?#?|part no\.?|item ?#?|mfr ?#?|qty|quantity|desc|description)\s*[:#]\s*([^\n]*)$/i.exec(c) ||
            /^(qty|quantity)\s+(\d{1,5}\s*\w{0,5})$/i.exec(c);                                 // "QTY 2" with no colon
          kv.push(mk ? mk[2] : c);
        });
        cs = kv;
      }
      // comma line with two or more short cells naming different products, and no wordy cell: one item per cell
      if (raw.indexOf('\t') < 0 && cs.length >= 2) {
        var keyed = cs.filter(function (c) { return split_ws(c).length <= 3 && split_ws(clean(c)).some(function (x) { return ix.is_key(x); }); });
        var strong_cell = function (c) {
          return split_ws(clean(c)).some(function (x) { return ix.is_key(x) && !short_num(norm(x)); }) || /(?:^|\s)(x\s*\d|\d+\s*x\b|\(\d+\))/i.test(c);
        };
        if (keyed.some(function (c) { return strong_cell(c); })) keyed = keyed.filter(function (c) { return strong_cell(c); });
        var wordy = cs.filter(function (c) { return words_of(/[A-Za-z]{3,}/g, c).length >= 2 && keyed.indexOf(c) < 0; });
        var prods = Object.create(null);
        keyed.forEach(function (c) { split_ws(clean(c)).forEach(function (t_) { if (ix.is_key(t_)) prods[products_of(t_, ix).join('\u0000')] = 1; }); });
        if (keyed.length >= 2 && Object.keys(prods).length >= 2 && !wordy.length) {
          cs.forEach(function (c) { read_free(c, ix).forEach(function (r) { r.raw = raw; r.split = true; out.push(r); }); });
          continue;
        }
      }
      var title_like = function (txt) {
        var dw = mkset(desc_words(txt).map(function (w) { return w.slice(0, 5); }));
        return dw.size >= 2 && ix.items.some(function (o) { return subset(dw, o.tw); });
      };
      if (raw.indexOf('\t') < 0 && cs.length >= 2 && !cs.some(function (c) { return QTY_CELL.test(c.trim()) || PRICE_CELL.test(c.trim()); }) &&
          !cs.some(function (c) { return split_ws(clean(c)).some(function (x) { return ix.is_key(x); }); }) && !title_like(raw)) {
        var groups = [];
        cs.forEach(function (c) {
          var words = words_of(/[A-Za-z]+/g, c);
          var attr = !words.length || words.every(function (w) { var wl = w.toLowerCase(); return ATTR_WORDS.has(wl) || (wl in UNITS) || MEASURE_FM.test(w); });
          if (attr && groups.length) groups[groups.length - 1] += ', ' + c;
          else groups.push(c);
        });
        if (groups.length >= 2 && groups.every(function (gr) { return /[A-Za-z]{2,}/.test(gr); })) {
          groups.forEach(function (gr) { read_free(gr, ix).forEach(function (r) { r.raw = raw; r.split = true; out.push(r); }); });
          continue;
        }
      }
      if (raw.indexOf('\t') < 0 && cs.length >= 2 && !hm && !cs.some(function (c) { return split_ws(clean(c)).some(function (x) { return ix.is_key(x); }); }) &&
          !cs.slice(0, -1).some(function (c) { return QTY_CELL.test(c.trim()); })) {
        read_free(raw, ix).forEach(function (r) { r.raw = raw; out.push(r); });                // "Mask, Medium, EA - 4": commas inside a name
        continue;
      }
      if (cs.length >= 2) {
        var qv = null, src = 'default', unit = null, parts = [], desc = '', extra = [];
        var cw = cs.map(W);                                       // cells as objects: Python compares some of them with "is"
        var prices = cw.filter(function (c) { return PRICE_CELL.test(c.s.trim()); }).map(function (c) { return c.s; });
        cw = cw.filter(function (c) { return prices.indexOf(c.s) < 0; });
        var is_qcell = function (c) { return QTY_CELL.test(c.s.trim()) && !ix.is_key(c.s); };
        var qcells = cw.filter(is_qcell);
        if (cw.length >= 3 && /^\d{1,3}$/.test(cw[0].s) && qcells.length >= 2 && !ix.is_key(cw[0].s)) {
          cw = cw.slice(1); qcells = cw.filter(is_qcell);       // line number
        }
        var flags = {};
        if (qcells.length) {
          var qc = qcells[qcells.length - 1], mqc = QTY_CELL.exec(qc.s.trim());
          qv = parseInt(mqc[1], 10); src = 'explicit'; unit = unit_of(mqc[2]);
          cw = cw.filter(function (c) { return c !== qc; });
          extra = qcells.slice(0, -1).filter(function (c) { return c.s.replace(/\D/g, '').length < 4; }).map(function (c) { return c.s; });
        } else if (cw.some(function (c) { return QTY_CELL.test(c.s.trim()) && ix.is_key(c.s) && short_num(norm(c.s)); }) &&
                   cw.some(function (c) { return ix.is_key(c.s) && !short_num(norm(c.s)); })) {
          var qc2 = cw.filter(function (c) { return QTY_CELL.test(c.s.trim()) && short_num(norm(c.s)); })[0];   // "AL-1033.C-EA, 350": 350 is the quantity
          qv = parseInt(/^\d+/.exec(qc2.s)[0], 10); src = 'explicit'; flags.qty_is_key = true;
          cw = cw.filter(function (c) { return c !== qc2; });
        }
        if (!cw.some(function (c) { return read_free(c.s, ix)[0].parts.length > 0; }) && qcells.length >= 2) {
          var zc = qcells.slice(0, -1).filter(function (c) { return ix.is_key(c.s, true); });
          if (zc.length) { cw = cw.concat(zc.slice(0, 1)); extra = extra.filter(function (c) { return c !== zc[0].s; }); }
        }
        var short_cells = cw.filter(function (c) { return split_ws(c.s).length <= 2; });
        var in_short = function (c) { return short_cells.some(function (x) { return x.s === c.s; }); };
        var part_cell = null;
        short_cells.concat(cw.filter(function (c) { return !in_short(c); })).some(function (c) {
          var ff = read_free(c.s, ix)[0];
          if (ff.parts.length && (in_short(c) || !short_cells.some(function (x) { return read_free(x.s, ix)[0].parts.length > 0; }))) {
            parts = ff.parts; part_cell = c;
            if (ff.desc) desc = ff.desc;                          // "Truck 350": the word stays with the line
            unit = unit || ff.unit; return true;
          }
          return false;
        });
        cw.forEach(function (c) {                                 // all other cells describe the item, in order
          var ff;
          if (c === part_cell) return;
          ff = read_free(c.s, ix)[0];
          unit = unit || ff.unit;
          desc = (desc + ' ' + c.s).trim();
        });
        if (!parts.length && !desc) desc = cw.map(function (c) { return c.s; }).join(' ');
        var itm = { raw: raw, parts: parts, desc: desc, text: [desc].concat(parts.filter(function (p) { return desc.indexOf(p) < 0; })).filter(Boolean).join(' '),
          qty: qv !== null ? qv : 1, src: src, unit: unit, extra: extra, whole: null,
          short: parts.length > 0 && parts.every(function (p) { return short_num(norm(p)); }) };
        if (flags.qty_is_key) itm.qty_is_key = true;
        out.push(itm); continue;
      }
      read_free(cells.length ? cells[0] : raw, ix).forEach(function (r) { r.raw = raw; out.push(r); });
    }
    // a signature without a sign-off: wordy lines after the last item with a part number, in an email-like paste
    var noisy = out.filter(function (o) { return o.noise; }).length, last = -1;
    out.forEach(function (o, i) { if (!o.noise && o.parts && o.parts.length) last = i; });
    if (noisy >= 2 && last >= 0) {
      out.slice(last + 1).forEach(function (o) {
        if (!o.noise && !(o.parts && o.parts.length) && o.src === 'default' && split_ws(o.raw).length <= 6 && !vocab_words(o.raw, ix).length) {
          o.noise = true; o.signature = true;
        }
      });
    }
    return out;
  }

  // ------------------------------------------------------------------ matching
  var CTX_SUB = /(size|sz|miller|mac|fr|french|gauge|ga|no\.?|number|box of|case of|pack of|bag of|qty)\s*(\d+(?:\.\d+)?)/g;
  function size_tokens(s) {
    /* Numbers with their meaning: "size 5" -> size5, "miller 3" -> miller3, "box of 5" -> boxof5, "22 g" -> 22g. */
    var out = new Set();
    s = replace_all(replace_all(s.toLowerCase(), ' x ', 'x'), '#', '');
    s = s.replace(CTX_SUB, function (m0, c, n) { return c.replace(/\W/g, '') + n; });
    s = s.replace(/(\d+(?:\.\d+)?)\s*\/\s*(bx|box|cs|case|pk|pack)/g, function (m0, n, u) { return n + 'per' + u; });
    words_of(/[a-z]*\d+(?:\.\d+)?(?:x\d+(?:\.\d+)?)*(?:\s?(?:fr|g|ga|mm|cm|in|ml|cc|ft|oz|kg|lb|gm|per(?:bx|box|cs|case|pk|pack)))?|[a-z]+/g, s).forEach(function (t) {
      t = replace_all(t, ' ', '');
      if (/\d/.test(t)) out.add(t.replace(/\.0+(?=\D|$)/g, ''));
      else if (SIZE_WORDS.has(t)) out.add(t);
    });
    return out;
  }
  var TWO_STOP = mkset(['of', 'to', 'in', 'on', 'at', 'by', 'or', 'an', 'is', 'it', 'be', 'as', 'up', 'no', 'we', 'us', 'me', 'my', 'do', 'go', 'so',
    'ea', 'bx', 'cs', 'pk', 'pr', 'dz', 'ok', 'hi', 're', 'pm', 'am', 'ft', 'mm', 'cm', 'ml', 'cc', 'oz', 'lb', 'kg', 'gm', 'fr', 'ga',
    'in', 'per', 'qty', 'nd', 'rd', 'th', 'st']);
  function desc_words(desc) {
    return words_of(/[a-z]{3,}/g, desc.toLowerCase()).filter(function (w) { return !STOP.has(w) && !SIZE_WORDS.has(w) && !FILLER.has(w); });
  }
  function two_words(desc) {                                  // set(w.lower() for w in 2-letter words) - TWO_STOP
    return mkset(two_letter_words(desc).map(function (w) { return w.toLowerCase(); }).filter(function (w) { return !TWO_STOP.has(w); }));
  }
  function tcount(o, dw, d2) {                                // len(tset(o) - dw - d2), tset = tw | (t2 - TWO_STOP)
    var seen = new Set(), n = 0;
    function add(w) { if (!seen.has(w) && !dw.has(w) && !d2.has(w)) n++; seen.add(w); }
    o.tw.forEach(function (w) { add(w); });
    o.t2.forEach(function (w) { if (!TWO_STOP.has(w)) add(w); });
    return n;
  }
  function agrees(it, desc, parts, ix) {
    /* The line's description must fit this product: EVERY word of it is in the title, every size/number read with its
       meaning ("size 5", "miller 3", "22g", "box of 5") is in the title, and no other product fits it better
       (Mac 3 vs Mac 3 Plus). */
    var words, title, named, two, pset, bad, pn, have, plain, dw, d2, extra, o, i, ok;
    if (!desc) return true;
    words = desc_words(desc);
    title = it.title.toLowerCase();
    named = (it.title + ' ' + it.vendor).toLowerCase();
    if (words.some(function (w) { return named.indexOf(w.slice(0, 5)) < 0; })) return false;
    two = two_words(desc);
    pset = mkset(parts.map(function (p) { return norm(p).toLowerCase(); }));
    bad = false;
    two.forEach(function (w) { if (!it.t2.has(w) && !pset.has(w)) bad = true; });                // "XL" vs an "SE" title
    if (bad) return false;
    pn = mkset(parts.map(function (p) { return norm(p).toLowerCase(); }).concat([it.ns.toLowerCase(), it.nm.toLowerCase()]));
    have = size_tokens(title);
    plain = mkset(words_of(/[A-Za-z0-9.\-]+/g, it.title).map(function (x) { return norm(x).toLowerCase(); }));
    ok = true;
    size_tokens(desc).forEach(function (s_) {
      if (!ok) return;
      if (pn.has(norm(s_).toLowerCase()) || have.has(s_)) return;
      if (!/^[a-z]/.test(s_) && plain.has(norm(s_).toLowerCase())) return;
      ok = false;
    });
    if (!ok) return false;
    if (ix != null && words.length >= 3) {
      dw = mkset(words.map(function (w) { return w.slice(0, 5); }));
      d2 = two_words(desc);
      extra = tcount(it, dw, d2);
      for (i = 0; i < ix.items.length; i++) {
        o = ix.items[i];
        if (o !== it && subset(dw, o.tw) && subset(d2, o.t2) && tcount(o, dw, d2) < extra && agrees(o, desc, parts)) return false;
      }
    }
    return true;
  }
  function ed1(a, b) {
    var la = a.length, lb = b.length, i = 0;
    if (Math.abs(la - lb) > 1) return false;
    while (i < la && i < lb && a.charAt(i) === b.charAt(i)) i++;
    if (la === lb) {
      if (a.slice(i + 1) === b.slice(i + 1)) return true;
      return i + 1 < la && a.charAt(i) === b.charAt(i + 1) && a.charAt(i + 1) === b.charAt(i) && a.slice(i + 2) === b.slice(i + 2);
    }
    return la > lb ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1);
  }
  function uniq(lst) {
    var seen = Object.create(null), o = [];
    lst.forEach(function (it) { if (it && !(it.h in seen)) { seen[it.h] = 1; o.push(it); } });
    return o;
  }
  function rank(opts, e) {
    /* Options only, never a lock: the items that fit the line's words and sizes come first, ties keep the search order
       ("intubrite miller 4 blade" puts Miller 4 first among the family's sizes, before the list is cut to 8). */
    var desc = e.desc || '', words, sizes, sc, idx;
    if (!desc || opts.length < 2) return opts;
    words = mkset(desc_words(desc).map(function (w) { return w.slice(0, 5); }));
    sizes = size_tokens(desc);
    sc = opts.map(function (it) {
      var named = (it.title + ' ' + it.vendor).toLowerCase(), have = size_tokens(it.title), n = 0,
        plain = mkset(words_of(/[A-Za-z0-9.\-]+/g, it.title).map(function (x) { return norm(x).toLowerCase(); }));
      words.forEach(function (w) { if (named.indexOf(w) > -1) n++; });
      sizes.forEach(function (s_) { if (have.has(s_) || (!/^[a-z]/.test(s_) && plain.has(norm(s_).toLowerCase()))) n += 2; });
      return n;
    });
    idx = opts.map(function (o, i) { return i; });
    idx.sort(function (a, b) { return sc[b] - sc[a] || a - b; });   // tie-break on position: stable on every browser
    return idx.map(function (i) { return opts[i]; });
  }
  function expand(ix, lst) {                                  // a listing-only family parent is replaced by its sizes
    var out = [];
    lst.forEach(function (it) {
      if (it.h in ix.listing) ix.listing[it.h].forEach(function (h) { if (h in ix.byh) out.push(ix.byh[h]); });
      else out.push(it);
    });
    return uniq(out);
  }
  function ask(search, q, fix) {                              // Search.get: blank queries are never sent
    q = (q == null ? '' : String(q)).trim();
    if (!q) return Promise.resolve({ items: [], fixed: null });
    return Promise.resolve(search(q, !!fix)).then(function (r) {
      return { items: (r && r.items) || [], fixed: r && r.fixed != null ? r.fixed : null };
    });
  }
  function set_values(s) { var a = []; s.forEach(function (x) { a.push(x); }); return a; }
  function match_(e, ix, S) {
    /* -> {state, options, flags, fixed}, or a Promise of it. state: locked | choose | check | phone | notfound | noise */
    var flags = [], hits = [], zero = [], it, kids, i, n, doubt, opts, ok_desc, ok_unit, dw, tiny, shared, short_ok, core, dw2, fits, q_part, fixed;
    if (e.noise) return { state: 'noise', options: [], flags: [], fixed: null };
    if (e.src === 'default') flags.push('qty_default');
    if (e.extra.length) flags.push('qty_extra');
    if (e.qty >= 1000) flags.push('qty_big');
    if (e.qty === 0) flags.push('qty_zero');
    if (e.qty_is_key) flags.push('qty_is_key');
    var unit = e.unit;
    if (unit === 'CONV') { flags.push('qty_converted'); unit = null; }                       // "2 dozen" read as 24: always shown, never locked (entry left as is)
    if (e.many_keys && e.many_keys.length) flags.push('many_keys');
    if (e.slash_pair && e.slash_pair.length) {
      opts = [];
      e.slash_pair.forEach(function (hs) { hs.forEach(function (h) { if (h in ix.byh) opts.push(ix.byh[h]); }); });
      return { state: 'check', options: uniq(opts), flags: flags.concat(['slash_pair']), fixed: null };
    }
    if (e.link) {
      it = ix.byh[e.link];
      if (it) {
        if (it.h in ix.listing) {
          kids = rank(ix.listing[it.h].filter(function (h) { return h in ix.byh; }).map(function (h) { return ix.byh[h]; }), e);
          return { state: kids.length ? 'choose' : 'phone', options: kids, flags: flags.concat(['family']), fixed: null };
        }
        doubt = flags.some(function (f) { return f === 'qty_extra' || f === 'qty_big' || f === 'qty_zero'; });
        return { state: doubt ? 'check' : 'locked', options: [it], flags: flags.concat(['link']), fixed: null };
      }
    }
    for (i = 0; i < e.parts.length; i++) {
      n = norm(e.parts[i]);
      if (n in ix.key) { hits = ix.key[n]; break; }
      if (lstrip0(n) in ix.bc) { hits = ix.bc[lstrip0(n)]; break; }
    }
    if (!hits.length) {
      for (i = 0; i < e.parts.length; i++) {
        n = norm(e.parts[i]);
        if (isdigit(n) && (lstrip0(n) in ix.z)) { zero = ix.z[lstrip0(n)]; break; }
      }
    }
    hits = uniq(hits);
    if (hits.length === 1) {
      it = hits[0];
      if (it.h in ix.listing) {
        kids = rank(expand(ix, [it]), e);
        return { state: kids.length ? 'choose' : 'phone', options: kids, flags: flags.concat(['family']), fixed: null };
      }
      ok_desc = agrees(it, e.sentence ? set_values(size_tokens(e.desc)).sort().join(' ') : e.desc, e.parts, ix);
      ok_unit = !(unit && it.pack && it.pack !== unit);
      dw = desc_words(e.desc);
      tiny = !!e.short && e.parts.every(function (p) { return norm(p).length <= 3 || /^\d0+$/.test(norm(p)); });
      shared = !!e.short && e.parts.some(function (p) { var l = ix.numtok[lstrip0(norm(p))]; return !!l && l.length > 1; });
      if (shared) {                                                  // "4108" is in MAS-4108 and in MAS-4108B's MPN: ask
        flags.push('shared_number');
        opts = [it];
        e.parts.forEach(function (p) { (ix.numtok[lstrip0(norm(p))] || []).forEach(function (h) { if (h in ix.byh) opts.push(ix.byh[h]); }); });
        return { state: 'check', options: expand(ix, uniq(opts)).slice(0, 8), flags: flags, fixed: null };
      }
      short_ok = !e.short || (!tiny && ((!dw.length && e.src === 'explicit') || (dw.length > 0 && ok_desc)));
      doubt = flags.some(function (f) { return f === 'qty_extra' || f === 'qty_big' || f === 'qty_zero' || f === 'qty_is_key' || f === 'many_keys' || f === 'qty_converted'; });
      if (ok_desc && ok_unit && short_ok && !doubt) return { state: 'locked', options: [it], flags: flags, fixed: null };
      opts = [it];
      if (!short_ok) flags.push('short_number');
      if (!ok_unit) {
        flags.push('unit_conflict');
        core = it.ns.replace(/(EA|BX|CS|PK|1EA)$/, '');
        opts = uniq((ix.core[core] || []).filter(function (s_) { return s_.pack === unit; }).concat(opts));
      }
      var done = function () {
        if (e.many_keys && e.many_keys.length) {
          e.many_keys.forEach(function (hs) { hs.forEach(function (h) { if (h in ix.byh) opts.push(ix.byh[h]); }); });
          opts = uniq(opts);
        }
        return { state: 'check', options: expand(ix, opts).slice(0, 8), flags: flags, fixed: null };
      };
      if (!ok_desc) {
        flags.push('desc_mismatch');
        dw2 = mkset(desc_words(e.desc).map(function (w) { return w.slice(0, 5); }));
        fits = sorted_by(ix.items.filter(function (o) { return dw2.size > 0 && subset(dw2, o.tw); }), function (o) {
          var c = 0; o.tw.forEach(function (w) { if (!dw2.has(w)) c++; }); return c;
        }).slice(0, 3);
        return ask(S, e.desc, false).then(function (r) {
          opts = uniq(opts.concat(fits).concat(r.items.filter(function (x) { return x.h in ix.byh; }).map(function (x) { return ix.byh[x.h]; }).slice(0, 4)));
          return done();
        });
      }
      return done();
    }
    if (hits.length > 1) return { state: 'choose', options: expand(ix, hits).slice(0, 8), flags: flags.concat(['same_number']), fixed: null };
    // nothing exact: everything below is options only
    opts = []; fixed = null;
    if (zero.length) opts = opts.concat(zero);
    if (e.whole && (e.whole in ix.key)) opts = opts.concat(ix.key[e.whole]);
    q_part = e.parts.length ? e.parts[0] : '';
    return (q_part ? ask(S, q_part, false).then(function (r) {
      var nq;
      opts = opts.concat(r.items.map(function (x) { return ix.byh[x.h]; }));
      nq = norm(q_part);
      if (nq.length >= 4) {
        var near = [];
        ix.keys.forEach(function (k) {
          if (k !== nq && Math.abs(k.length - nq.length) <= 1 && ed1(nq, k)) ix.key[k].forEach(function (x) { near.push(x); });
        });
        opts = opts.concat(near.slice(0, 6));
        opts = opts.concat(ix.items.filter(function (x) { return (x.ns && x.ns.indexOf(nq) >= 0) || (x.nm && x.nm.indexOf(nq) >= 0); }).slice(0, 4));
      }
    }) : Promise.resolve()).then(function () {
      var full = (e.text || e.desc || '').trim(), words, res;
      if (!(full && norm(full) !== norm(q_part))) return null;
      return ask(S, full, false).then(function (r) {
        res = r.items;
        words = split_ws(full).filter(function (w) { return /[A-Za-z]{3,}/.test(w); }).join(' ');
        return (!res.length && words && words !== full) ? ask(S, words, false).then(function (r2) { res = r2.items; }) : null;
      }).then(function () {
        if (res.length < 3 && words && !/\d/.test(words)) {
          return ask(S, words, true).then(function (r3) {      // Searchanise's typo correction, options only, never on part numbers
            fixed = r3.fixed;
            if (r3.items.length) {
              res = res.concat(r3.items);
              if (fixed && fixed.toLowerCase() !== words.toLowerCase()) flags.push('corrected');
            }
          });
        }
        return null;
      }).then(function () {
        opts = res.map(function (x) { return ix.byh[x.h]; }).concat(opts);
      });
    }).then(function () {
      opts = rank(expand(ix, opts.filter(Boolean)), e).slice(0, 8);
      if (!opts.length) return { state: 'notfound', options: [], flags: flags, fixed: null };
      return { state: 'choose', options: opts, flags: flags, fixed: fixed };
    });
  }

  function buildIndex(rows) { return new Index(rows); }
  function readBlock(text, ix) { return read_block(String(text == null ? '' : text), ix); }
  function match(entry, ix, search) {
    return new Promise(function (resolve) { resolve(match_(entry, ix, search)); });
  }
  var STATES = ['locked', 'choose', 'check', 'phone', 'notfound', 'noise'];
  var QTY_FLAGS = ['qty_default', 'qty_extra', 'qty_big', 'qty_zero', 'qty_is_key', 'qty_converted'];
  var FLAGS = QTY_FLAGS.concat(['many_keys', 'slash_pair', 'family', 'link', 'shared_number', 'short_number', 'unit_conflict',
    'desc_mismatch', 'corrected', 'same_number']);
  var api = { buildIndex: buildIndex, readBlock: readBlock, match: match, norm: norm, clean: clean, FLAGS: FLAGS, STATES: STATES, QTY_FLAGS: QTY_FLAGS };
  if (typeof window !== 'undefined') window.PBMQuickOrderEngine = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
